import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import CookieSettingsModal from './CookieSettingsModal';

const CookieBanner: React.FC = () => {
    const {
        consentStatus,
        acceptCookies,
        rejectCookies,
        isModalOpen,
        openSettings,
        closeSettings,
    } = useCookieConsent();

    // Delay showing the banner slightly for better UX
    const [shouldShow, setShouldShow] = useState(false);

    useEffect(() => {
        if (consentStatus === 'unknown') {
            const timer = setTimeout(() => setShouldShow(true), 1000);
            return () => clearTimeout(timer);
        } else {
            setShouldShow(false);
        }
    }, [consentStatus]);

    return (
        <>
            <AnimatePresence>
                {shouldShow && (
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        className="fixed bottom-4 left-4 right-4 z-[50] md:left-auto md:right-8 md:max-w-[480px]"
                    >
                        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 p-6 relative overflow-hidden">
                            {/* Decorative elements */}
                            <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                                <Cookie className="w-32 h-32 -mr-8 -mt-8 rotate-12" />
                            </div>

                            <div className="relative z-10 flex flex-col gap-4">
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-primary/10 rounded-xl">
                                        <Cookie className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                                            La SAPI respecte votre vie privée
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                            Avec votre accord, nous utilisons des cookies ou technologies similaires pour stocker, consulter et traiter des données personnelles telles que votre visite sur ce site internet, les adresses IP et les identifiants de cookie.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 mt-2">
                                    <button
                                        onClick={acceptCookies}
                                        className="w-full py-2.5 px-4 bg-primary hover:bg-primary-hover text-white font-medium rounded-lg shadow-lg shadow-primary/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                                    >
                                        Accepter & Fermer
                                    </button>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            onClick={rejectCookies}
                                            className="py-2.5 px-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium rounded-lg border border-gray-200 dark:border-gray-700 transition-all active:scale-[0.98]"
                                        >
                                            Continuer sans accepter
                                        </button>
                                        <button
                                            onClick={openSettings}
                                            className="py-2.5 px-4 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 font-medium rounded-lg border border-gray-200 dark:border-gray-700 transition-all active:scale-[0.98]"
                                        >
                                            En savoir plus
                                        </button>
                                    </div>
                                </div>

                                <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-2">
                                    À tout moment, vous pouvez retirer votre consentement dans notre politique de confidentialité.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <CookieSettingsModal
                isOpen={isModalOpen}
                onClose={closeSettings}
                onAcceptAll={() => {
                    acceptCookies();
                    closeSettings();
                }}
                onRejectAll={() => {
                    rejectCookies();
                    closeSettings();
                }}
            />
        </>
    );
};

export default CookieBanner;
